import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import {
    deleteExtDataGroup,
    getExtDataGroup,
    getExtDataGroups,
    setExtDataGroup,
    updateExtDataGroupsOrder,
    setOpenModal,
    setFilters,
} from '../../store/extDataGroups/actions';
import constants from '../../commonUtils/constants';
import IconButton from '../../components/IconButton';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';

const ExtDataGroupsContext = createContext();

export const useExtDataGroups = () => useContext(ExtDataGroupsContext);

export const ExtDataGroupsProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { filters, ExtDataGroupsList, loading, errors, openModal } = useSelector((state) => state.ExtDataGroups);
    const { extractionViewOnly, extractionData } = useSelector((state) => state.Extractions);
    const { extVersion } = useSelector((state) => state.ExtVersions);
    const versionId = extVersion?.id || extVersion?._id;

    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId] = useState("");
    const [localExtDataGroups, setLocalExtDataGroups] = useState([]);

    const debouncedGetExtDataGroups = useMemo(() => debounce((params) => {
        dispatch(getExtDataGroups(params));
    }, 300), [dispatch]);

    // Sync versionId and is_draft into Redux filters whenever scope changes
    useEffect(() => {
        if (!extractionData?.id) return;

        const updatedFilters = {
            ...filters,
            is_draft: extractionViewOnly && versionId ? false : true,
            versionId: extractionViewOnly && versionId ? versionId : null,
        };

        dispatch(setFilters(updatedFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extractionViewOnly, versionId, extractionData?.id]);

    // Fetch whenever filters change (filters now includes versionId + is_draft)
    useEffect(() => {
        if (!extractionData?.id) return;
        debouncedGetExtDataGroups({ extId: extractionData.id, ...filters });
    }, [filters, extractionData?.id, debouncedGetExtDataGroups]);

    const extDataGroups = useMemo(() => {
        return ExtDataGroupsList?.data?.map(
            (item, index) => ({
                id: `${item._id}_${index}`,
                _id: item._id,
                key: item.key,
                title: item.title,
                query: item.query,
                fields: item.fields,
                originalIndex: index + 1,
            })
        ) || [];
    }, [ExtDataGroupsList]);

    useEffect(() => {
        setLocalExtDataGroups([]);
    }, [ExtDataGroupsList?.data]);

    const displayData = localExtDataGroups.length > 0 ? localExtDataGroups : extDataGroups;

    const pagination = useMemo(() => ExtDataGroupsList?.pagination, [ExtDataGroupsList]);

    const handlePageChange = useCallback((currentPage, perPage) => {
        debouncedGetExtDataGroups({ extId: extractionData?.id, currentPage, perPage, ...filters });
    }, [filters, debouncedGetExtDataGroups, extractionData?.id]);

    const handleRowReorder = useCallback((reorderedData) => {
        const updatedData = reorderedData.map((item, index) => ({
            ...item,
            priority: index + 1,
        }));

        setLocalExtDataGroups(updatedData);

        const orderData = updatedData.map((item, index) => ({
            _id: item._id,
            priority: index + 1
        }));

        dispatch(updateExtDataGroupsOrder(orderData));
    }, [dispatch]);

    const getRowId = useCallback((row) => {
        return row.id?.toString() || row._id?.toString() || '';
    }, []);

    const editExtDataGroup = useCallback((id) => {
        dispatch(setExtDataGroup(null));
        dispatch(setOpenModal(true));
        dispatch(getExtDataGroup(id));
    }, [dispatch]);

    const openAddModal = useCallback(() => {
        dispatch(setExtDataGroup(null));
        dispatch(setOpenModal(true));
    }, [dispatch]);

    const handleCloseModal = useCallback((action) => {
        if (action === 'delete') {
            setShowDeleteConfirmationDialog(false);
            setToDeleteId("");
        } else {
            dispatch(setOpenModal(false));
            dispatch(setExtDataGroup(null));
        }
    }, [dispatch]);

    const askDeleteConfirmation = useCallback((id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    }, []);

    const handleDelete = useCallback(() => {
        dispatch(deleteExtDataGroup(toDeleteId));
        setToDeleteId("");
        setShowDeleteConfirmationDialog(false);
    }, [dispatch, toDeleteId]);

    const resetOrder = useCallback(() => {
        setLocalExtDataGroups([]);
    }, []);

    const columnHelper = createColumnHelper();
    const columns = useMemo(() => [
        columnHelper.accessor('originalIndex', { header: '#', cell: info => info.row.index + 1 }),
        columnHelper.accessor('key', { header: 'Key', cell: info => info.getValue() }),
        columnHelper.accessor('title', { header: 'Title', cell: info => info.getValue() }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            cell: info => (
                <>
                    <IconButton
                        disabled={extractionViewOnly}
                        variant="action_default"
                        size="icon"
                        Icon={PencilSquareIcon}
                        onClick={() => editExtDataGroup(info.row.original._id)}
                    />
                    <IconButton
                        disabled={extractionViewOnly}
                        variant="action_default"
                        size="icon"
                        Icon={TrashIcon}
                        onClick={() => askDeleteConfirmation(info.row.original._id)}
                    />
                </>
            )
        }),
    ], [askDeleteConfirmation, columnHelper, editExtDataGroup, extractionViewOnly]);

    const table = useReactTable({
        data: displayData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getRowId: getRowId,
        initialState: {
            pagination: {
                pageIndex: (ExtDataGroupsList?.pagination?.currentPage || 1) - 1,
                pageSize: ExtDataGroupsList?.pagination?.perPage || constants.tableUI.defaultPerpage,
            },
        },
        manualPagination: true,
        pageCount: ExtDataGroupsList?.pagination?.totalPages,
    });

    return (
        <ExtDataGroupsContext.Provider value={{
            extDataGroups: displayData,
            table,
            columns,
            loading,
            errors,
            pagination,
            handlePageChange,
            editExtDataGroup,
            openAddModal,
            openModal,
            handleCloseModal,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
            handleRowReorder,
            getRowId,
            resetOrder,
        }}>
            {children}
        </ExtDataGroupsContext.Provider>
    );
};